/**
 * Member Routes — Require authentication (ProtectedRoute)
 * Personal pages, pet management, member features
 */
import React, { Suspense } from 'react';
import { Route } from 'react-router-dom';

import Feed from '../pages/Feed';
import Messages from '../pages/Messages';
import Settings from '../pages/Settings';
import Files from '../pages/Files';
import Interests from '../pages/Interests';
import Friends from '../pages/Friends';
import MyPets from '../pages/MyPets';
import CareTeam from '../pages/CareTeam';
import MedicalRecords from '../pages/MedicalRecords';
import PetFriends from '../pages/PetFriends';
import FamilyTree from '../pages/FamilyTree';
import GeneticHealthChecklist from '../pages/GeneticHealthChecklist';
import HealthDetection from '../pages/HealthDetection';
import PromoPageEditor from '../pages/PromoPageEditor';
import PrivateForums from '../pages/PrivateForums';
import MemberDashboard from '../pages/MemberDashboard';
import MembershipUpgrade from '../pages/MembershipUpgrade';
import SearchPage from '../pages/Search';
import ClubResources from '../pages/ClubResources';
import SuggestClub from '../pages/SuggestClub';
import CreateSubGroup from '../pages/CreateSubGroup';
import SubmitServiceProvider from '../pages/SubmitServiceProvider';
import SubmitDogPark from '../pages/SubmitDogPark';
import BetaFeedbackList from '../pages/BetaFeedbackList';
import BetaTestPlan from '../pages/BetaTestPlan';
import BusinessCard from '../pages/BusinessCard';
import HandoutCard from '../pages/HandoutCard';
import ReferCard from '../pages/ReferCard';
import ProposalsList from '../pages/ProposalsList';
import NewProposal from '../pages/NewProposal';
import ProposalDetail from '../pages/ProposalDetail';

// Lazy
const Referrals = React.lazy(() => import('../pages/Referrals'));
const PetCalendar = React.lazy(() => import('../pages/PetCalendar'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export function getMemberRoutes(Layout, ProtectedRoute) {
  return [
    // Dashboard & Core
    <Route key="/dashboard" path="/dashboard" element={<ProtectedRoute><Layout><MemberDashboard /></Layout></ProtectedRoute>} />,
    <Route key="/feed" path="/feed" element={<ProtectedRoute><Layout><Feed /></Layout></ProtectedRoute>} />,
    <Route key="/messages" path="/messages" element={<ProtectedRoute><Layout><Messages /></Layout></ProtectedRoute>} />,
    <Route key="/settings" path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />,
    <Route key="/settings/blocked" path="/settings/blocked" element={<ProtectedRoute><Layout><Interests /></Layout></ProtectedRoute>} />,
    <Route key="/files" path="/files" element={<ProtectedRoute><Layout><Files /></Layout></ProtectedRoute>} />,
    <Route key="/interests" path="/interests" element={<ProtectedRoute><Layout><Interests /></Layout></ProtectedRoute>} />,
    <Route key="/friends" path="/friends" element={<ProtectedRoute><Layout><Friends /></Layout></ProtectedRoute>} />,
    <Route key="/search" path="/search" element={<Layout><SearchPage /></Layout>} />,

    // Pets
    <Route key="/my-pets" path="/my-pets" element={<ProtectedRoute><Layout><MyPets /></Layout></ProtectedRoute>} />,
    <Route key="/care-team" path="/care-team" element={<ProtectedRoute><Layout><CareTeam /></Layout></ProtectedRoute>} />,
    <Route key="/medical-records" path="/medical-records" element={<ProtectedRoute><Layout><MedicalRecords /></Layout></ProtectedRoute>} />,
    <Route key="/pet-friends" path="/pet-friends" element={<ProtectedRoute><Layout><PetFriends /></Layout></ProtectedRoute>} />,
    <Route key="/family-tree" path="/family-tree" element={<ProtectedRoute><Layout><FamilyTree /></Layout></ProtectedRoute>} />,
    <Route key="/genetic-health/:petId" path="/genetic-health/:petId" element={<ProtectedRoute><Layout><GeneticHealthChecklist /></Layout></ProtectedRoute>} />,
    <Route key="/ai-health" path="/ai-health" element={<ProtectedRoute><Layout><HealthDetection /></Layout></ProtectedRoute>} />,

    // Membership & Club
    <Route key="/membership/upgrade" path="/membership/upgrade" element={<ProtectedRoute><Layout><MembershipUpgrade /></Layout></ProtectedRoute>} />,
    <Route key="/club-resources" path="/club-resources" element={<ProtectedRoute><Layout><ClubResources /></Layout></ProtectedRoute>} />,
    <Route key="/suggest-club" path="/suggest-club" element={<ProtectedRoute><Layout><SuggestClub /></Layout></ProtectedRoute>} />,
    <Route key="/communities/create" path="/communities/create" element={<ProtectedRoute><Layout><CreateSubGroup /></Layout></ProtectedRoute>} />,
    <Route key="/pro-forums" path="/pro-forums" element={<ProtectedRoute><Layout><PrivateForums /></Layout></ProtectedRoute>} />,
    <Route key="/my-promo-page" path="/my-promo-page" element={<ProtectedRoute><Layout><PromoPageEditor /></Layout></ProtectedRoute>} />,
    <Route key="/business-card" path="/business-card" element={<ProtectedRoute><Layout><BusinessCard /></Layout></ProtectedRoute>} />,
    <Route key="/handout-card" path="/handout-card" element={<ProtectedRoute><Layout><HandoutCard /></Layout></ProtectedRoute>} />,
    <Route key="/refer-card" path="/refer-card" element={<ProtectedRoute><Layout><ReferCard /></Layout></ProtectedRoute>} />,
    <Route key="/proposals" path="/proposals" element={<ProtectedRoute><Layout><ProposalsList /></Layout></ProtectedRoute>} />,
    <Route key="/proposals/new" path="/proposals/new" element={<ProtectedRoute><Layout><NewProposal /></Layout></ProtectedRoute>} />,
    <Route key="/proposals/:id" path="/proposals/:id" element={<ProtectedRoute><Layout><ProposalDetail /></Layout></ProtectedRoute>} />,

    // Submissions
    <Route key="/submit-service" path="/submit-service" element={<ProtectedRoute><Layout><SubmitServiceProvider /></Layout></ProtectedRoute>} />,
    <Route key="/submit-park" path="/submit-park" element={<ProtectedRoute><Layout><SubmitDogPark /></Layout></ProtectedRoute>} />,

    // Beta
    <Route key="/beta-feedback" path="/beta-feedback" element={<ProtectedRoute><Layout><BetaFeedbackList /></Layout></ProtectedRoute>} />,
    <Route key="/beta-test-plan" path="/beta-test-plan" element={<ProtectedRoute><Layout><BetaTestPlan /></Layout></ProtectedRoute>} />,

    // Lazy
    <Route key="/referrals" path="/referrals" element={<ProtectedRoute><Layout><Suspense fallback={<PageLoader />}><Referrals /></Suspense></Layout></ProtectedRoute>} />,
    <Route key="/calendar" path="/calendar" element={<ProtectedRoute><Layout pageTitle="Pet Calendar"><Suspense fallback={<PageLoader />}><PetCalendar /></Suspense></Layout></ProtectedRoute>} />,
  ];
}
